
import { Component, Model, Prop, Vue, Watch } from 'vue-property-decorator';
import FormItems from './FormItems.vue';

export interface StepperStep {
  key?: string;
  title: string | (() => string);
  subtitle?: string | (() => string);
  disabled?: () => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  complete?: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface StepperSlotProps {
  step: StepperStep;
  index: number;
  position: StepperPosition;
}

export interface StepperPosition {
  gotoStep: (step: number) => void;
  gotoNext: () => void;
  gotoPrev: () => void;
  hasNext: boolean;
  hasPrev: boolean;
}

@Component({ components: { FormItems } })
export default class extends Vue {
  @Model('change', { type: Number, default: 1 }) model!: number;

  @Prop() readonly steps!: StepperStep[];

  @Prop() readonly title!: string;

  @Prop() readonly contentClass!: string | string[] | HashAny;

  @Prop({ type: Object }) readonly data!: HashAny;

  currentStep = 1;

  get stepObject() {
    if (!this.steps || !this.steps.length) {
      return undefined;
    }
    const idx = this.currentStep - 1;
    return this.steps[idx];
  }

  get slotName() {
    if (!this.steps || !this.steps.length) {
      return 'default';
    }
    const name = this.stepObject?.key;
    if (name == null || name === '') {
      return `step-${this.currentStep}`;
    }
    return `${name}`;
  }

  get lastStep() {
    return (this.steps || []).length;
  }

  get prevStep() {
    return Math.max(1, this.currentStep - 1);
  }

  get hasPrev() {
    return this.currentStep > this.prevStep;
  }

  get nextStep() {
    return Math.min(this.lastStep, this.currentStep + 1);
  }

  get hasNext() {
    return this.currentStep < this.nextStep;
  }

  get position(): StepperPosition {
    return {
      gotoStep: this.gotoStep,
      gotoNext: this.gotoNext,
      gotoPrev: this.gotoPrev,
      hasNext: this.hasNext,
      hasPrev: this.hasPrev,
    };
  }

  paddedIndex(step: number): string {
    return step < 10 ? `0${step}` : `${step}`;
  }

  gotoStep(stepOrStepKey: number | string) {
    let step: number;
    if (typeof stepOrStepKey === 'string') {
      const index = this.steps.findIndex(s => s.key === stepOrStepKey);
      if (index === -1) {
        return;
      }
      step = index + 1;
    } else {
      step = stepOrStepKey;
    }
    if (!Number.isFinite(step)) {
      return;
    }
    const value = this.boundStep(step);
    if (this.currentStep !== value) {
      this.currentStep = value;
      this.$emit('change', value);
    }
  }

  gotoPrev() {
    this.$emit('goto-prev', this.prevStep);
    this.gotoStep(this.prevStep);
  }

  gotoNext() {
    this.$emit('goto-next', this.nextStep);
    this.gotoStep(this.nextStep);
  }

  boundStep(step: number) {
    if (step < 1) {
      return 1;
    }
    if (step > this.lastStep) {
      return this.lastStep;
    }
    return step;
  }

  isStepDisabled(step: number) {
    const idx = step - 1;
    const obj = this.steps[idx];
    if (typeof obj.disabled === 'function' && obj.disabled()) {
      return true;
    }
    const prev = this.steps[idx - 1];
    if (!prev) {
      return false;
    }
    return !this.isComplete(prev);
  }

  isComplete(step: StepperStep) {
    if (step.complete) {
      return !!step.complete();
    }
    return true;
  }

  created() {
    if (this.model) {
      this.onModelChanged(this.model);
    }
  }

  @Watch('model')
  onModelChanged(newValue: number) {
    if (newValue == null) {
      return;
    }
    let value = (typeof newValue === 'number' && newValue) || 1;
    value = this.boundStep(value);
    if (this.currentStep !== value) {
      this.currentStep = value;
    }
  }
}
